/**
 * CallDetail — Phase 7 Sub-iter 7G.
 *
 * Full-page view for a single call_log entry, now on the canonical
 * EntityShell pattern.  Tabs:
 *   Overview — type/direction/duration/notes editor + linked-entity strip
 *   Activity — HistoryBlock audit trail
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Phone, MessageCircle, Mail, Calendar, FileText, Save, Loader2,
  ExternalLink, Clock, ArrowRight, AlertTriangle,
} from "lucide-react";
import api, { formatErr } from "../lib/api";
import VoiceTextarea from "../components/VoiceTextarea";
import { EntityShell } from "../components/shell";
import HistoryBlock from "../components/shell/HistoryBlock";

const TYPES = [
  { v: "phone", label: "Phone call", icon: Phone, color: "#1FB6C1" },
  { v: "whatsapp", label: "WhatsApp", icon: MessageCircle, color: "#22C55E" },
  { v: "sms", label: "SMS", icon: MessageCircle, color: "#F59E0B" },
  { v: "email", label: "Email", icon: Mail, color: "#A78BFA" },
  { v: "meeting", label: "Meeting", icon: Calendar, color: "#06B6D4" },
  { v: "other", label: "Other", icon: FileText, color: "#647087" },
];

export default function CallDetail() {
  const { id } = useParams();
  const [call, setCall] = useState(null);
  const [linked, setLinked] = useState({ customer: null, lead: null, jobcard: null, ticket: null });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const [tab, setTab] = useState("overview");

  const load = async () => {
    try {
      const { data } = await api.get(`/calls/${id}`);
      setCall(data);
      const tasks = [];
      if (data.customer_id) tasks.push(api.get(`/customers/${data.customer_id}`).then((r) => ({ customer: r.data })).catch(() => ({})));
      if (data.linked_lead_id) tasks.push(api.get(`/leads`).then((r) => ({ lead: (r.data || []).find((l) => l.id === data.linked_lead_id) })).catch(() => ({})));
      if (data.linked_jobcard_id) tasks.push(api.get(`/jobcards`).then((r) => ({ jobcard: (r.data || []).find((j) => j.id === data.linked_jobcard_id) })).catch(() => ({})));
      if (data.linked_ticket_id) tasks.push(api.get(`/tickets`).then((r) => ({ ticket: (r.data || []).find((t) => t.id === data.linked_ticket_id) })).catch(() => ({})));
      const results = await Promise.all(tasks);
      setLinked(results.reduce((acc, r) => ({ ...acc, ...r }), { customer: null, lead: null, jobcard: null, ticket: null }));
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not load call"));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const save = async () => {
    setBusy(true); setErr("");
    try {
      await api.put(`/calls/${id}`, {
        notes: call.notes,
        type: call.type,
        direction: call.direction,
        duration_min: Number(call.duration_min) || 0,
      });
      setSavedAt(new Date().toISOString());
      setTimeout(() => setSavedAt(null), 3000);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Save failed"));
    } finally { setBusy(false); }
  };

  if (!call) {
    return (
      <div className="p-6 space-y-3">
        <div className="text-sm text-[#647087]">Loading call…</div>
        {err && <div className="hv-card border-rose-500/40 bg-rose-500/5 p-3 text-xs text-rose-300">{err}</div>}
      </div>
    );
  }

  const meta = TYPES.find((t) => t.v === call.type) || TYPES[0];
  const Icon = meta.icon;
  const titleSubject = linked.customer?.name || call.customer_name || "Loose call (no customer linked)";
  const TONE = { phone: "blue", whatsapp: "green", sms: "amber", email: "violet", meeting: "blue", other: "slate" };

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "activity", label: "Activity" },
  ];
  const headerActions = savedAt
    ? <span className="text-xs text-emerald-300" data-testid="call-detail-saved-pill">Saved ✓</span>
    : null;

  return (
    <EntityShell
      kindLabel={`${meta.label} · ${call.direction === "in" ? "Incoming" : "Outgoing"}`}
      title={titleSubject}
      status={{ label: meta.label.toLowerCase(), tone: TONE[meta.v] || "slate" }}
      customerLink={linked.customer ? {
        label: `View customer · ${linked.customer.name}`,
        to: `/customers/${linked.customer.id}`,
      } : undefined}
      backTo="/calls"
      actions={headerActions}
      tabs={tabs}
      activeTab={tab}
      onTabChange={setTab}
    >
      <div className="space-y-4 hv-fade-in" data-testid="call-detail-page">
        {tab === "overview" && (
          <>
            <div className="hv-card p-5" data-testid="call-detail-hero">
              <div className="flex items-start gap-3 flex-wrap">
                <div
                  className="w-12 h-12 rounded-md flex items-center justify-center border shrink-0"
                  style={{ borderColor: `${meta.color}55`, background: `${meta.color}1a`, color: meta.color }}
                >
                  <Icon className="w-6 h-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-[#a0aabf] flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(call.created_at).toLocaleString("en-GB")}</span>
                    {call.duration_min > 0 && <span>{call.duration_min}m talk-time</span>}
                    <span>by <span className="text-[#1fb6c1]">{call.by_user_name}</span></span>
                  </div>
                </div>
              </div>

              {(linked.customer || linked.lead || linked.jobcard || linked.ticket) && (
                <div className="flex items-center gap-2 flex-wrap pt-3 mt-3 border-t border-white/5">
                  {linked.customer && (
                    <Link to={`/customers/${linked.customer.id}`} className="hv-btn hv-btn-ghost text-xs" data-testid="call-detail-link-customer">
                      <ExternalLink className="w-3 h-3 text-[#1FB6C1]" /> Customer · {linked.customer.name}
                    </Link>
                  )}
                  {linked.lead && (
                    <Link to={`/leads/${linked.lead.id}`} className="hv-btn hv-btn-ghost text-xs" data-testid="call-detail-link-lead">
                      <ArrowRight className="w-3 h-3 text-[#A78BFA]" /> Lead · {linked.lead.title}
                    </Link>
                  )}
                  {linked.jobcard && (
                    <Link to={`/jobcards/${linked.jobcard.id}`} className="hv-btn hv-btn-ghost text-xs" data-testid="call-detail-link-jobcard">
                      <ArrowRight className="w-3 h-3 text-[#22C55E]" /> Jobcard · {linked.jobcard.title}
                    </Link>
                  )}
                  {linked.ticket && (
                    <Link to={`/tickets/${linked.ticket.id}`} className="hv-btn hv-btn-ghost text-xs" data-testid="call-detail-link-ticket">
                      <ArrowRight className="w-3 h-3 text-[#F59E0B]" /> Ticket · {linked.ticket.title}
                    </Link>
                  )}
                </div>
              )}
            </div>

            {err && (
              <div className="hv-card border-rose-500/40 bg-rose-500/5 p-3 text-xs text-rose-300 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" /> {err}
              </div>
            )}

            <div className="hv-card p-4 space-y-3" data-testid="call-detail-edit">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider text-[#647087]">Type</span>
                  <select
                    value={call.type}
                    onChange={(e) => setCall({ ...call, type: e.target.value })}
                    className="hv-input mt-1"
                    data-testid="call-edit-type"
                  >
                    {TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider text-[#647087]">Direction</span>
                  <select
                    value={call.direction}
                    onChange={(e) => setCall({ ...call, direction: e.target.value })}
                    className="hv-input mt-1"
                    data-testid="call-edit-direction"
                  >
                    <option value="out">Outgoing</option>
                    <option value="in">Incoming</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-wider text-[#647087]">Duration (min)</span>
                  <input
                    type="number"
                    value={call.duration_min || 0}
                    onChange={(e) => setCall({ ...call, duration_min: e.target.value })}
                    className="hv-input mt-1"
                    data-testid="call-edit-duration"
                  />
                </label>
              </div>
              <div>
                <span className="text-[10px] uppercase tracking-wider text-[#647087]">Notes</span>
                <VoiceTextarea
                  value={call.notes || ""}
                  onChange={(v) => setCall({ ...call, notes: v })}
                  rows={6}
                  className="mt-1"
                  context="lead_activity"
                  testIdPrefix="call-edit-notes-voice"
                  data-testid="call-edit-notes"
                  placeholder="What was discussed, action items, next steps… or tap the mic."
                />
              </div>
              <div className="flex justify-end">
                <button onClick={save} disabled={busy} className="hv-btn hv-btn-primary" data-testid="call-edit-save">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  Save changes
                </button>
              </div>
            </div>
          </>
        )}

        {tab === "activity" && (
          <HistoryBlock entity={{ id, kind: "call" }} />
        )}
      </div>
    </EntityShell>
  );
}
